/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IProductDimensionConfigureDropdownViewProps, IProductDimensionConfigureViewProps } from '@msdyn365-commerce/components';
import { INodeProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IQuickOrderGridFormViewProps } from './components';
import { IQuickOrderGridViewProps } from './quick-order-grid';

/**
 * Render product dimension/configure dropdown.
 * @param dropdown -- Product dimension drop down.
 * @returns Dropdown.
 */
const renderConfigureDropdown = (dropdown: IProductDimensionConfigureDropdownViewProps): JSX.Element => {
    const { containerProps, labelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...containerProps}>
            <Node {...labelContainerProps}>
                {heading}
            </Node>
            {errors}
            {select}
        </Node>
    );
};

/**
 * Render product dimension/configure container.
 * @param configure -- Product dimension configuration.
 * @returns Container to have dimension dropdown.
 */
const renderConfigure = (configure?: IProductDimensionConfigureViewProps): JSX.Element | null => {
    if (configure !== undefined) {
        const { containerProps, dropdowns } = configure;

        return (
            <Node {...containerProps}>
                {dropdowns.map(renderConfigureDropdown)}
            </Node>
        );
    }
    return null;

};

/**
 * Render product dimension/configure container.
 * @param form -- Form props.
 * @param isMobileView -- Flag to check mobile view.
 * @param quickOrderForm - Form component container.
 * @param quickOrderProductContent - Form product component.
 * @param quickOrderMessageContent - Form user message component.
 * @param configure - Product Dimension component.
 * @returns Jsx element of quick order form component.
 */
const renderForm = (form: IQuickOrderGridFormViewProps, isMobileView: boolean,
    quickOrderForm: INodeProps, quickOrderProductContent: INodeProps, quickOrderMessageContent: INodeProps,
    configure?: IProductDimensionConfigureViewProps) => {
    return isMobileView ? (
        <Node {...quickOrderForm}>
            <Node {... quickOrderProductContent}>
                {form.product}
                {renderConfigure(configure)}
                {form.quantity}
                <Node {...quickOrderMessageContent}>
                    {form.userMessage}
                </Node>
                {form.addToListBtn}
            </Node>
        </Node>
    ) : (
        <Node {...quickOrderForm}>
            <Node {...quickOrderMessageContent}>
                {form.userMessage}
            </Node>
            <Node {... quickOrderProductContent}>
                {form.product}
                {renderConfigure(configure)}
                {form.quantity}
                {form.addToListBtn}
            </Node>
        </Node>
    );
};

/**
 * Retrieves current sort criteria.
 * @param props - QuickOrder Grid view props.
 * @returns - Render View.
 */
const quickOrderGridView: React.FC<IQuickOrderGridViewProps> = props => {

    const {
        isB2bCustomer,
        quickOrderGridProps,
        contentProps,
        form,
        quickOrderGridForm,
        drawer,
        configure,
        quickOrderGridFormProductContent,
        quickOrderGridFormMsgContent,
        isMobileView,
        header,
        container,
        actions,
        orderActions
    } = props;

    return isB2bCustomer ? (
        <Module {...quickOrderGridProps}>
            {header}
            {actions && (
                <Node {...actions}>
                    {orderActions?.addProductListToBag}
                    {orderActions?.removeProductList}
                </Node>
            )}
            <Node {...drawer}>
                <Node {...contentProps}>
                    {renderForm(form as IQuickOrderGridFormViewProps, isMobileView, quickOrderGridForm,
                        quickOrderGridFormProductContent, quickOrderGridFormMsgContent, configure)}
                </Node>
            </Node>
            {container}
        </Module>
    ) : null;
};

export default quickOrderGridView;
